/**
 * VideoPostCard Component
 * 
 * A reusable card component that displays individual video post information including:
 * - Video title, thumbnail, and link
 * - Publication status (Ready/Draft) with visual indicators
 * - Author, language, and video link information
 * - Progress indicators and statistics (transcripts count, watch time)
 * - Tags display
 * - Click handler for navigation to transcript management
 * - Menu button for additional actions (edit, delete)
 * 
 * Used in: VideoPostList component
 */
import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Avatar,
    LinearProgress,
    Link
} from '@mui/material';
import { MoreVert, CheckCircle, Warning, PlayArrow, AccessTime } from '@mui/icons-material';
import PropTypes from 'prop-types';

const VideoPostCard = ({ post, onCardClick, onMenuClick }) => {
    if (!post) {
        return null;
    }

    const hasTranscripts = post.transcripts && post.transcripts.length > 0;
    const completionProgress = post.completionProgress || 0;

    return (
        <Paper 
            onClick={() => onCardClick(post)}
            sx={{ 
                p: 0,
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                }
            }}
        >
            <Box sx={{ p: 2 }}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                    {/* Thumbnail */}
                    <Avatar 
                        src={post.thumbUrl} 
                        variant="square" 
                        sx={{ width: 56, height: 56, flexShrink: 0 }}
                    >
                        <PlayArrow fontSize="large" />
                    </Avatar>
                    
                    {/* Main Content */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box display="flex" alignItems="center" gap={1} sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }} noWrap>
                                    {post.title}
                                </Typography>
                                <Tooltip title={post.isReadyToServe ? "Ready to serve to users" : "Not ready to serve"}>
                                    {post.isReadyToServe ? (
                                        <CheckCircle color="success" fontSize="small" />
                                    ) : (
                                        <Warning color="warning" fontSize="small" />
                                    )}
                                </Tooltip>
                            </Box>
                            
                            <Tooltip title="More options">
                                <IconButton
                                    size="small"
                                    onClick={(e) => onMenuClick(e, post)}
                                    sx={{ ml: 1, flexShrink: 0 }}
                                >
                                    <MoreVert />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        
                        {/* Author, Language, and Level */}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {post.author && `by ${post.author}`}
                            {post.author && (post.language?.name || post.level?.name) && ' • '}
                            {post.language?.name}
                            {post.language?.name && post.level?.name && ' • '}
                            {post.level?.name && `${post.level.name}`}
                            {post.isReadyToServe ? (
                                <Chip 
                                    label="Ready" 
                                    size="small" 
                                    color="success" 
                                    variant="outlined"
                                    sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                                />
                            ) : (
                                <Chip 
                                    label="Draft" 
                                    size="small" 
                                    color="warning" 
                                    variant="outlined"
                                    sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                                />
                            )}
                        </Typography>
                        
                        {/* Video Link */}
                        <Box sx={{ mb: 1 }}>
                            <Link 
                                href={post.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{ 
                                    fontSize: '0.8rem',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {post.link}
                            </Link>
                        </Box>
                        
                        {/* Stats */}
                        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {hasTranscripts ? post.transcripts.length : 0} transcripts
                            </Typography>
                            {post.estimatedWatchTime > 0 && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <AccessTime fontSize="small" sx={{ color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {post.estimatedWatchTime}m
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        
                        {/* Translation Progress */}
                        {hasTranscripts && (
                            <Box sx={{ mb: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Translation Progress
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {completionProgress}%
                                    </Typography>
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={completionProgress} 
                                    sx={{ height: 4, borderRadius: 2 }}
                                />
                            </Box>
                        )}
                        
                        {/* Tags */}
                        {post.tags && post.tags.length > 0 && (
                            <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                                {post.tags.slice(0, 3).map((tag, index) => (
                                    <Chip 
                                        key={index}
                                        label={tag} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: '20px' }}
                                    />
                                ))}
                                {post.tags.length > 3 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                        +{post.tags.length - 3} more
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

VideoPostCard.propTypes = {
    post: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        link: PropTypes.string.isRequired,
        thumbUrl: PropTypes.string,
        author: PropTypes.string,
        language: PropTypes.object,
        level: PropTypes.shape({
            _id: PropTypes.string,
            name: PropTypes.string
        }),
        isReadyToServe: PropTypes.bool,
        transcripts: PropTypes.array,
        estimatedWatchTime: PropTypes.number,
        completionProgress: PropTypes.number,
        tags: PropTypes.array
    }).isRequired,
    onCardClick: PropTypes.func.isRequired,
    onMenuClick: PropTypes.func.isRequired
};

export default VideoPostCard; 